import type { AudioOnset, ChartAudio } from '../chart.js';
import type { Variant } from '../gameTypes.js';
import { VARIANT_TUNING } from '../gameTypes.js';

const VALID_KINDS = new Set<AudioOnset['kind']>(['kick', 'snare', 'lead']);

interface RawOnset {
  beat: number;
  kind: string;
  intensity: number;
}

export interface RawOnsetsFile {
  slug: string;
  bpm: number;
  offsetMs: number;
  durationSec: number;
  onsets: RawOnset[];
}

export interface BuiltinAudioInit {
  src: string;
  attribution: string;
  data: RawOnsetsFile;
}

function narrowOnsets(raw: RawOnset[]): AudioOnset[] {
  const out: AudioOnset[] = [];
  for (const o of raw) {
    if (!VALID_KINDS.has(o.kind as AudioOnset['kind'])) {
      throw new Error(`onset has unknown kind '${o.kind}' (beat ${o.beat})`);
    }
    out.push({ beat: o.beat, kind: o.kind as AudioOnset['kind'], intensity: o.intensity });
  }
  return out;
}

/**
 * Build a ChartAudio from an imported onsets JSON. Centralised so every
 * builtin chart wires audio identically; if we later change the audio
 * URL convention or add a CDN base, only this one helper changes.
 */
export function buildBuiltinAudio({ src, attribution, data }: BuiltinAudioInit): ChartAudio {
  return {
    src,
    bpm: data.bpm,
    offsetMs: data.offsetMs,
    durationSec: data.durationSec,
    attribution,
    onsets: narrowOnsets(data.onsets),
  };
}

/**
 * Filter helper: returns the beats (sorted, deduplicated) where any onset
 * of the given kinds occurs. Builtin charts use this to pull authoritative
 * "musical hit beats" out of the onset list, then snap pads to them.
 */
export function beatsForKinds(
  data: RawOnsetsFile,
  kinds: ReadonlyArray<AudioOnset['kind']>,
): number[] {
  const set = new Set<string>(kinds);
  const beats = new Set<number>();
  for (const onset of data.onsets) {
    if (set.has(onset.kind)) {
      beats.add(onset.beat);
    }
  }
  return [...beats].sort((a, b) => a - b);
}

/**
 * Rich chart-authoring helper. Pulls a subset of onset beats out of the
 * full list according to a section filter. Designed so per-chart files
 * can compose musical sections like:
 *
 *   selectBeats(onsets, { fromBeat: 0, untilBeat: 16, kinds: ['kick'] })
 *   selectBeats(onsets, { fromBeat: 16, untilBeat: 32, kinds: ['snare'], everyNth: 2 })
 *   selectBeats(onsets, { fromBeat: 32, untilBeat: 48, minIntensity: 0.4, minSpacing: 0.5 })
 *
 * which produces musically-varied pad placement (sparse verse → syncopated
 * pre-chorus → dense drop) without an explicit per-pad list.
 *
 * Options:
 *   - fromBeat / untilBeat: half-open [from, until) range. Default = whole song.
 *   - kinds: only keep onsets of these kinds. Default = all kinds.
 *   - minIntensity: only keep onsets at or above this strength (0..1).
 *   - everyNth: pick 1-of-N matching onsets in this range (e.g. 2 = half).
 *   - minSpacing: enforce at least this many beats between picked beats.
 *     Useful for capping density on rapid leads (a 0.5 spacing rejects
 *     anything tighter than an 8th note).
 */
export interface SelectBeatsOpts {
  fromBeat?: number;
  untilBeat?: number;
  kinds?: ReadonlyArray<AudioOnset['kind']>;
  minIntensity?: number;
  everyNth?: number;
  minSpacing?: number;
}

export function selectBeats(data: RawOnsetsFile, opts: SelectBeatsOpts = {}): number[] {
  const {
    fromBeat = -Infinity,
    untilBeat = Infinity,
    kinds,
    minIntensity = 0,
    everyNth = 1,
    minSpacing = 0,
  } = opts;
  const kindsSet = kinds ? new Set<string>(kinds) : null;
  const sorted = data.onsets.slice().sort((a, b) => a.beat - b.beat);
  const out: number[] = [];
  let strideCounter = 0;
  let lastPicked = -Infinity;
  for (const o of sorted) {
    if (o.beat < fromBeat) continue;
    if (o.beat >= untilBeat) break;
    if (kindsSet && !kindsSet.has(o.kind)) continue;
    if (o.intensity < minIntensity) continue;
    // Spacing check BEFORE the stride counter increments. Otherwise a beat
    // that's too close to the last pick would still consume a stride slot
    // and effectively skip the next valid candidate, which is rarely what
    // an author wants when combining the two flags.
    if (o.beat - lastPicked < minSpacing) continue;
    strideCounter += 1;
    if (strideCounter % everyNth !== 0) continue;
    out.push(o.beat);
    lastPicked = o.beat;
  }
  return out;
}

/**
 * Combine multiple selectBeats results, dedupe, and return a sorted
 * pad-beat array. Charts use this to layer sections together:
 *
 *   const padBeats = mergeBeats(
 *     selectBeats(o, { fromBeat: 0, untilBeat: 16, ... }),  // intro
 *     selectBeats(o, { fromBeat: 16, untilBeat: 48, ... }), // verse
 *     selectBeats(o, { fromBeat: 48, untilBeat: 96, ... }), // drop
 *   );
 */
export function mergeBeats(...lists: ReadonlyArray<ReadonlyArray<number>>): number[] {
  const seen = new Set<number>();
  for (const list of lists) {
    for (const b of list) {
      seen.add(b);
    }
  }
  return [...seen].sort((a, b) => a - b);
}

/**
 * Like `mergeBeats` but enforces a minimum gap between consecutive picks
 * across the merged sources. Each `selectBeats` call applies its own
 * `minSpacing` independently; if you merge a kick selection and a snare
 * selection that each respect 0.5 beats internally, the union can still
 * produce 0.25-beat-apart pads where one kick lands right next to one
 * snare. At 174 BPM that's 86 ms - too tight to physically alternate.
 *
 * Use this whenever combining kick + snare (or any two onset kinds) at
 * high BPMs to enforce a global playability floor.
 */
export function mergeBeatsWithSpacing(
  minSpacingBeats: number,
  ...lists: ReadonlyArray<ReadonlyArray<number>>
): number[] {
  const sorted = mergeBeats(...lists);
  if (minSpacingBeats <= 0) {
    return sorted;
  }
  const out: number[] = [];
  let lastPicked = -Infinity;
  for (const b of sorted) {
    if (b - lastPicked < minSpacingBeats) continue;
    out.push(b);
    lastPicked = b;
  }
  return out;
}

/**
 * Variant-aware wrapper around {@link selectBeats}. The chart's "Normal"
 * tuning lives in the `opts` argument verbatim; Hard and Extreme variants
 * scale `minIntensity` and `minSpacing` by {@link VARIANT_TUNING} factors
 * to recruit weaker / closer onsets as pads. All other knobs (kinds,
 * fromBeat, untilBeat, everyNth) pass through unchanged because they
 * select *which* onsets to consider — the variant only changes *how much*
 * of that pool to keep.
 */
export function variantSelectBeats(
  variant: Variant,
  data: RawOnsetsFile,
  opts: SelectBeatsOpts = {},
): number[] {
  const v = VARIANT_TUNING[variant];
  return selectBeats(data, {
    ...opts,
    minIntensity: (opts.minIntensity ?? 0) * v.intensityFactor,
    minSpacing: (opts.minSpacing ?? 0) * v.spacingFactor,
  });
}

/**
 * Variant-aware wrapper around {@link mergeBeatsWithSpacing}. The post-merge
 * spacing floor scales by the same factor as `selectBeats`'s `minSpacing`,
 * so kick+snare layered drops get progressively tighter at higher variants.
 */
export function variantMergeBeatsWithSpacing(
  variant: Variant,
  minSpacingBeats: number,
  ...lists: ReadonlyArray<ReadonlyArray<number>>
): number[] {
  return mergeBeatsWithSpacing(
    minSpacingBeats * VARIANT_TUNING[variant].spacingFactor,
    ...lists,
  );
}

export interface FindBurstChambersOpts {
  /** Inclusive start of the search range. Defaults to 0. */
  fromBeat?: number;
  /** Exclusive end of the search range. Defaults to +Infinity. */
  untilBeat?: number;
  /**
   * Smallest gap between consecutive pad beats that should become a
   * chamber. Defaults to 8. Smaller gaps stay as natural breathing room.
   */
  minGapBeats?: number;
  /**
   * Beats of margin to leave on each side of a chamber so the player has
   * recovery time after a pad and lookahead time before the next one.
   * Defaults to 1.5.
   */
  marginBeats?: number;
  /**
   * Hard cap on chamber duration. Beyond this we'd need a second chamber
   * (which would feel weird inside a single empty stretch). Defaults to
   * 8 - long enough to sustain a mash without becoming exhausting.
   */
  maxLengthBeats?: number;
}

/**
 * Scan a sorted list of pad beats and return chamber ranges for every
 * gap >= `minGapBeats`. Each chamber is centred in the gap with
 * `marginBeats` of breathing room on either side and capped at
 * `maxLengthBeats`. Chambers shorter than {@link MIN_BURST_CHAMBER_BEATS}
 * are dropped.
 *
 * The "before the first pad" and "after the last pad" stretches are also
 * considered (using `fromBeat` / `untilBeat` as their other edge), so a
 * long intro with no pads still becomes a chamber.
 */
export function findBurstChambers(
  padBeats: ReadonlyArray<number>,
  opts: FindBurstChambersOpts = {},
): Array<{ startBeat: number; endBeat: number }> {
  const fromBeat = opts.fromBeat ?? 0;
  const untilBeat = opts.untilBeat ?? Infinity;
  const minGapBeats = opts.minGapBeats ?? 8;
  const marginBeats = opts.marginBeats ?? 1.5;
  const maxLengthBeats = opts.maxLengthBeats ?? 8;
  const minLengthBeats = 2;

  const sorted = padBeats
    .filter((b) => b >= fromBeat && b < untilBeat)
    .sort((a, b) => a - b);

  const out: Array<{ startBeat: number; endBeat: number }> = [];
  const edges = [fromBeat, ...sorted, untilBeat];
  for (let i = 0; i < edges.length - 1; i++) {
    const left = edges[i];
    const right = edges[i + 1];
    if (!Number.isFinite(left) || !Number.isFinite(right)) continue;
    const gap = right - left;
    if (gap < minGapBeats) continue;
    const usable = gap - marginBeats * 2;
    if (usable < minLengthBeats) continue;
    const length = Math.min(maxLengthBeats, usable);
    const startBeat = left + marginBeats;
    const endBeat = startBeat + length;
    out.push({ startBeat, endBeat });
  }
  return out;
}
